// ---------------------------------------------------------------------------
// D35: Inbox Statistics Dashboard
// ---------------------------------------------------------------------------
// Metrics: avg response time, resolution time, volume by channel,
// conversations per staff, open/closed ratio.

import { useState, useEffect } from "react";
import { apiFetch } from "@/lib/api-client";
import { Clock, MessageCircle, Users, TrendingUp, BarChart3 } from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

interface InboxStats {
  avgFirstResponseMinutes: number;
  avgResolutionMinutes: number;
  totalConversations: number;
  openConversations: number;
  closedConversations: number;
  byChannel: Record<string, number>;
  byStaff: Array<{ userId: string; name: string; count: number; avgResponseMinutes: number }>;
  csatScore: number | null;
}

export default function InboxStatsPage() {
  const [stats, setStats] = useState<InboxStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [period, setPeriod] = useState("7d");

  useEffect(() => {
    setIsLoading(true);
    apiFetch<{ data: InboxStats }>(`/crm/inbox-stats?period=${period}`)
      .then((res) => setStats(res.data))
      .catch(() => setStats(null))
      .finally(() => setIsLoading(false));
  }, [period]);

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonCard key={i} className="h-24" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">Inbox Analytics</h1>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm"
        >
          <option value="1d">Today</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
        </select>
      </div>

      {!stats ? (
        <p className="text-sm text-muted-foreground">No data available for this period.</p>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard
              icon={<Clock className="h-4 w-4" />}
              label="Avg First Response"
              value={`${stats.avgFirstResponseMinutes}m`}
            />
            <StatCard
              icon={<Clock className="h-4 w-4" />}
              label="Avg Resolution"
              value={`${stats.avgResolutionMinutes}m`}
            />
            <StatCard
              icon={<MessageCircle className="h-4 w-4" />}
              label="Conversations"
              value={String(stats.totalConversations)}
              subtitle={`${stats.openConversations} open · ${stats.closedConversations} closed`}
            />
            {stats.csatScore != null && (
              <StatCard
                icon={<TrendingUp className="h-4 w-4" />}
                label="CSAT Score"
                value={`${stats.csatScore}%`}
              />
            )}
          </div>

          {/* Channel breakdown */}
          <div className="stagger-item card-hover rounded-lg border border-border p-4">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
              <BarChart3 className="h-4 w-4 text-[var(--color-primary)]" />
              Volume by Channel
            </h3>
            <div className="space-y-2">
              {Object.entries(stats.byChannel).map(([channel, count]) => (
                <div key={channel} className="flex items-center justify-between text-sm">
                  <span className="capitalize text-muted-foreground">{channel}</span>
                  <div className="flex items-center gap-2">
                    <div className="h-2 rounded-full bg-[var(--color-primary)]" style={{ width: `${Math.min(200, (count / stats.totalConversations) * 200)}px` }} />
                    <span className="font-medium text-foreground" style={{ fontVariantNumeric: "tabular-nums" }}>{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Staff performance */}
          {stats.byStaff.length > 0 && (
            <div className="stagger-item card-hover rounded-lg border border-border p-4">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
                <Users className="h-4 w-4 text-[var(--color-primary)]" />
                Per Staff Member
              </h3>
              <div className="divide-y divide-border">
                {stats.byStaff.map((staff) => (
                  <div key={staff.userId} className="flex items-center justify-between py-2 text-sm">
                    <span className="font-medium text-foreground">{staff.name}</span>
                    <div className="flex items-center gap-4 text-muted-foreground" style={{ fontVariantNumeric: "tabular-nums" }}>
                      <span>{staff.count} conversations</span>
                      <span>{staff.avgResponseMinutes}m avg response</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, subtitle }: { icon: React.ReactNode; label: string; value: string; subtitle?: string }) {
  return (
    <div className="stagger-item card-hover rounded-lg border border-border p-4">
      <div className="flex items-center gap-2 text-muted-foreground">
        {icon}
        <span className="text-xs font-medium">{label}</span>
      </div>
      <p className="mt-1 text-2xl font-bold text-foreground" style={{ fontVariantNumeric: "tabular-nums" }}>{value}</p>
      {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
    </div>
  );
}
